import type { RevisionContent, Timing } from '../../domain'

/**
 * Los acordes de la canción en el orden en que se tocan: la secuencia de interpretación, con las
 * repeticiones. Tocar se mueve por esta lista (siguiente acorde, anterior, saltar de sección).
 * Todo apunta a IDs de aparición, línea y acorde, nunca a posiciones de pantalla (regla 3).
 */
export interface ChordEvent {
  occurrenceId: string
  lineId: string
  chordId: string
  symbol: string
  /** Posición de la línea en la hoja, contando todas las líneas (también las sin acordes). */
  order: number
  /** Posición de la aparición dentro de la secuencia. */
  occurrenceIndex: number
}

export interface Timeline {
  events: ChordEvent[]
  /** Orden de cada línea de la hoja, por «aparición:línea». */
  orderOf: Map<string, number>
}

export function buildEvents(content: RevisionContent): Timeline {
  const sections = new Map(content.sections.map((section) => [section.id, section]))
  const events: ChordEvent[] = []
  const orderOf = new Map<string, number>()
  let order = 0
  content.sequence.forEach((occurrence, occurrenceIndex) => {
    for (const line of sections.get(occurrence.section)?.lines ?? []) {
      orderOf.set(`${occurrence.id}:${line.id}`, order)
      for (const anchor of line.chords) {
        events.push({
          occurrenceId: occurrence.id,
          lineId: line.id,
          chordId: anchor.id,
          symbol: anchor.chord,
          order,
          occurrenceIndex,
        })
      }
      order += 1
    }
  })
  return { events, orderOf }
}

/**
 * El evento al que lleva pulsar un acorde o una línea. Una línea sin acordes lleva al primer
 * acorde que viene después; si no hay ninguno, no lleva a ninguna parte (−1).
 */
export function indexForPick(
  timeline: Timeline,
  occurrenceId: string,
  lineId: string,
  chordId: string | null,
): number {
  const { events, orderOf } = timeline
  if (chordId !== null) {
    return events.findIndex(
      (event) => event.occurrenceId === occurrenceId && event.chordId === chordId,
    )
  }
  const target = orderOf.get(`${occurrenceId}:${lineId}`)
  if (target === undefined) return -1
  return events.findIndex((event) => event.order >= target)
}

/** El siguiente acorde que se vea distinto del actual (los repetidos seguidos no cuentan). */
export function nextDistinct(
  events: readonly ChordEvent[],
  index: number,
  show: (symbol: string) => string,
): ChordEvent | undefined {
  const current = events[index]
  if (!current) return events[0]
  const shown = show(current.symbol)
  return events.slice(index + 1).find((event) => show(event.symbol) !== shown)
}

/** Primer evento de cada aparición, para los botones de salto. Las apariciones sin acordes no salen. */
export function firstEventOfOccurrences(events: readonly ChordEvent[]): Map<string, number> {
  const first = new Map<string, number>()
  events.forEach((event, index) => {
    if (!first.has(event.occurrenceId)) first.set(event.occurrenceId, index)
  })
  return first
}

/**
 * Salta de sección. Hacia delante, al primer acorde de la aparición siguiente. Hacia atrás, al
 * principio de la aparición actual y, si ya estás en él, al de la anterior.
 */
export function jumpSection(events: readonly ChordEvent[], index: number, delta: 1 | -1): number {
  const current = events[index]
  if (!current) return index
  const starts = [...firstEventOfOccurrences(events).values()].sort((a, b) => a - b)
  if (delta === 1) {
    return starts.find((start) => start > index) ?? index
  }
  const here = starts.filter((start) => start <= index).pop() ?? 0
  if (here < index) return here
  return starts.filter((start) => start < index).pop() ?? index
}

/**
 * Correspondencia entre los acordes de la hoja (`events`) y los eventos del mapa de tiempos. Las
 * dos listas siguen la secuencia, pero el mapa puede tener silencios: un silencio se muestra en el
 * último acorde que sonó.
 */
export interface EventLinks {
  /** Para cada evento del mapa, el acorde de la hoja que se marca. */
  sheetOfTimed: number[]
  /** Para cada acorde de la hoja, su evento en el mapa (−1 si no tiene). */
  timedOfSheet: number[]
}

export function linkEvents(events: readonly ChordEvent[], timing: Timing): EventLinks {
  const key = (occurrence: string, chord: string) => `${occurrence}:${chord}`
  const sheetByKey = new Map(
    events.map((event, index) => [key(event.occurrenceId, event.chordId), index]),
  )
  const timedByKey = new Map<string, number>()
  let last = events.length > 0 ? 0 : -1
  const sheetOfTimed = timing.events.map((timed, index) => {
    if (timed.chord === null) return last
    timedByKey.set(key(timed.occurrence, timed.chord), index)
    last = sheetByKey.get(key(timed.occurrence, timed.chord)) ?? last
    return last
  })
  const timedOfSheet = events.map(
    (event) => timedByKey.get(key(event.occurrenceId, event.chordId)) ?? -1,
  )
  return { sheetOfTimed, timedOfSheet }
}
